/**
 * Phase 51 Plan 51-07 INTAKE-02 — mutation hook for moving a triage task
 * to the first open state.
 *
 * Mirrors the useUpdateTaskTier / useCreateExternalTask mutation + invalidate
 * shape. On settle, invalidates the internal board cache so the card leaves
 * the Triage column and appears in the first real state column.
 *
 * Backend endpoint (Plan 06):
 *   POST /api/tasks/:taskId/intake/move-to-open
 *   Requires: task.assigneeId set; task.tierId set on SLA projects (else 422).
 *   422 error codes: task_intake_assignee_required, task_tier_required.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { moveTaskToOpen } from '../services/api/board';

export interface MoveTaskToOpenInput {
  taskId: number;
  projectId: string;
}

/**
 * Mutation hook calling POST /api/tasks/:taskId/intake/move-to-open.
 * Invalidates ['board', projectId, 'internal'] on settle so the Triage
 * column refreshes and the card moves to the first real state.
 */
export function useMoveTaskToOpen() {
  const queryClient = useQueryClient();

  return useMutation<
    Awaited<ReturnType<typeof moveTaskToOpen>>,
    Error,
    MoveTaskToOpenInput
  >({
    mutationFn: ({ taskId }) => moveTaskToOpen(taskId),
    onSettled: (_data, _err, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: ['board', projectId, 'internal'],
      });
    },
  });
}
